import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Eye,
  Boxes,
  ArrowRight,
  UserCheck,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AssignReviewerModal from "@/components/questions/AssignReviewerModal";
import DeclineBatchModal from "@/components/questions/DeclineBatchModal";
import ConfirmDialog from "@/components/ConfirmDialog";

const STATUS_STYLES = {
  unassigned: "bg-slate-100 text-slate-600",
  assigned: "bg-blue-50 text-blue-700",
  in_review: "bg-amber-50 text-amber-700",
  submitted: "bg-purple-50 text-purple-700",
  qc_complete: "bg-emerald-50 text-emerald-700",
  completed: "bg-emerald-50 text-emerald-700",
  active: "bg-orange-50 text-orange-700",
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "unassigned", label: "Unassigned" },
  { value: "assigned", label: "Assigned" },
  { value: "in_review", label: "In review" },
  { value: "submitted", label: "Submitted (awaiting QC)" },
  { value: "qc_complete", label: "QC complete" },
];

export default function Batches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [assignBatch, setAssignBatch] = useState(null);
  const [declineBatch, setDeclineBatch] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const isReviewer = user?.role === "sme_reviewer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/batches");
      setBatches(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () => (statusFilter ? batches.filter((b) => b.status === statusFilter) : batches),
    [batches, statusFilter]
  );

  const selectableIds = useMemo(
    () => filtered.filter((b) => b.status === "unassigned").map((b) => b.id),
    [filtered]
  );

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectableIds.every((id) => selected.has(id)) && selectableIds.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const del = (b) => {
    setConfirmState({
      title: "Delete this batch?",
      message: (
        <span>
          You're about to delete <strong>{b.name}</strong>.<br />
          Any unreviewed questions will be returned to the raw pool. Approved questions remain.
        </span>
      ),
      confirmLabel: "Delete batch",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/batches/${b.id}`);
          toast.success("Batch deleted");
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const bulkDelete = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setConfirmState({
      title: `Delete ${ids.length} batch${ids.length === 1 ? "" : "es"}?`,
      message: (
        <span>
          Their questions go back to the raw pool. Only <strong>unassigned</strong> batches will be
          deleted — any batch already assigned to a reviewer is automatically skipped.
        </span>
      ),
      confirmLabel: `Delete ${ids.length}`,
      danger: true,
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/batches/bulk-delete", { batch_ids: ids });
          if (data.deleted > 0) {
            toast.success(`Deleted ${data.deleted} batch${data.deleted === 1 ? "" : "es"}`);
          }
          if (data.skipped && data.skipped.length > 0) {
            toast.warning(
              `Skipped ${data.skipped.length} batch${data.skipped.length === 1 ? "" : "es"} (in use)`
            );
          }
          clearSelection();
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const showBulkBar = isAdmin && selected.size > 0;
  const allSelectableChecked =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  return (
    <PageContainer>
      <PageHeader
        title={isReviewer ? "My Review Batches" : "Batches"}
        subtitle="Reviewer assignments and progress"
      />

      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-5 flex flex-wrap items-center gap-2">
        {isAdmin && selectableIds.length > 0 && (
          <label
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 cursor-pointer text-xs font-mono text-slate-600 hover:bg-slate-50"
            data-testid="batches-select-all-label"
          >
            <input
              type="checkbox"
              checked={allSelectableChecked}
              onChange={toggleSelectAll}
              data-testid="batches-select-all"
              className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
            Select all unassigned ({selectableIds.length})
          </label>
        )}
        <select
          data-testid="batches-status-filter"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            clearSelection();
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="text-xs font-mono text-slate-400 ml-auto">
          Showing {filtered.length} of {batches.length}
        </div>
      </div>

      {showBulkBar && (
        <div
          data-testid="bulk-action-bar"
          className="mb-4 flex items-center justify-between gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200"
        >
          <div className="text-sm text-orange-900 font-semibold">
            {selected.size} batch{selected.size === 1 ? "" : "es"} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="text-xs text-orange-700 hover:underline font-semibold"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
            <button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-semibold"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-12 text-center">
          <Boxes className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 font-semibold mb-1" style={{ fontFamily: "Outfit" }}>
            No batches{statusFilter ? ` with status "${statusFilter}"` : ""}
          </div>
          <div className="text-sm text-slate-400">
            {isAdmin
              ? "Auto-batch the raw pool or select questions in Raw Questions to assign."
              : "Your admin hasn't assigned any batches to you yet."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((b) => (
            <BatchCard
              key={b.id}
              b={b}
              isAdmin={isAdmin}
              isReviewer={isReviewer}
              isSelected={selected.has(b.id)}
              onToggleSelect={() => toggleSelect(b.id)}
              onOpen={() => navigate(`/batches/${b.id}`)}
              onAssign={() => setAssignBatch(b)}
              onDecline={() => setDeclineBatch(b)}
              onDelete={() => del(b)}
            />
          ))}
        </div>
      )}

      {assignBatch && (
        <AssignReviewerModal
          batch={assignBatch}
          onClose={() => setAssignBatch(null)}
          onAssigned={() => {
            setAssignBatch(null);
            load();
          }}
        />
      )}

      {declineBatch && (
        <DeclineBatchModal
          batch={declineBatch}
          onClose={() => setDeclineBatch(null)}
          onDeclined={() => {
            setDeclineBatch(null);
            load();
          }}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}

function BatchCard({
  b,
  isAdmin,
  isReviewer,
  isSelected,
  onToggleSelect,
  onOpen,
  onAssign,
  onDecline,
  onDelete,
}) {
  const pct = b.total_questions ? Math.round((b.reviewed_count / b.total_questions) * 100) : 0;
  const isUnassigned = b.status === "unassigned";
  const isAssigned = b.status === "assigned" || b.status === "in_review";
  const isDeclined = isUnassigned && b.declined_reason;
  return (
    <div
      data-testid={`batch-card-${b.id}`}
      className={`bg-white rounded-xl border brand-shadow p-5 hover:brand-shadow-hover transition flex flex-col ${
        isSelected
          ? "border-orange-300 ring-2 ring-orange-200"
          : isDeclined
          ? "border-amber-300 bg-amber-50/30"
          : "border-slate-100"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {isAdmin && isUnassigned && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              data-testid={`batch-select-${b.id}`}
              className="mt-1 rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
          )}
          <div className="min-w-0">
            <div className="font-bold text-slate-900 truncate" style={{ fontFamily: "Outfit" }}>
              {b.name}
            </div>
            <div className="text-[11px] font-mono text-slate-400 mt-0.5">
              {b.total_questions} questions
              {b.subject ? ` · ${b.subject}` : ""}
              {b.created_by_name ? ` · by ${b.created_by_name}` : ""}
            </div>
          </div>
        </div>
        <span
          className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider ${
            isDeclined
              ? "bg-amber-100 text-amber-800"
              : STATUS_STYLES[b.status] || "bg-slate-100 text-slate-600"
          }`}
        >
          {isDeclined ? "Declined" : b.status?.replace("_", " ")}
        </span>
      </div>

      {b.declined_reason && isUnassigned && (
        <div className="mb-2 p-2 rounded-md bg-amber-50 border border-amber-100 text-[11px] text-amber-900 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <div>
            <strong>Declined</strong> by {b.declined_by || "reviewer"}: {b.declined_reason}
          </div>
        </div>
      )}

      <div className="mb-1.5 flex items-center justify-between text-xs text-slate-500 font-mono">
        <span>Reviewer: {b.reviewer_name || "—"}</span>
        <span>
          {b.reviewed_count} reviewed / {b.total_questions}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between gap-2">
        <button
          onClick={onOpen}
          data-testid={`batch-open-btn-${b.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-semibold"
        >
          {isReviewer ? (
            <>
              {isAssigned ? "Start review" : "View"} <ArrowRight className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              <Eye className="w-3.5 h-3.5" /> View details
            </>
          )}
        </button>
        <div className="flex items-center gap-1">
          {isAdmin && isUnassigned && (
            <button
              onClick={onAssign}
              data-testid={`batch-assign-btn-${b.id}`}
              title="Assign to reviewer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100"
            >
              <UserCheck className="w-3.5 h-3.5" /> Assign
            </button>
          )}
          {isReviewer && isAssigned && (
            <button
              onClick={onDecline}
              data-testid={`batch-decline-btn-${b.id}`}
              title="Decline batch"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100"
            >
              <XCircle className="w-3.5 h-3.5" /> Decline
            </button>
          )}
          {isAdmin && (
            <button
              onClick={onDelete}
              data-testid={`batch-delete-btn-${b.id}`}
              title="Delete batch"
              className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
